import { i18n } from '@lt/localization';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import MobileAppBanner from 'src/components/MobileAppBanner';
import Modal from 'src/components/Modal';

import { NBSP } from 'src/constants/common';

import { Monitor } from '@lt/components/icons-react/sidebar';
import { setMobileCookie } from 'src/utils/mobileUtils';
import { Link } from '@lt/components';
import { useFreezeBody } from '@lt/components/hooks';
import { getMobileMenu } from '../../utils/getMobileMenu';

import { MOBILE_PLATFORMS, WRAPPER_TEST_ID } from './constants';
import type { ISideNavMenu } from './types';

export const SideNavMenu = ({
  isWhitelabel = false,
  closeSidebar,
  onShowAuthPopup,
  onLogOut,
  isLoggedIn = false,
  onOpenWriteUsPopup,
  isHideFeedback,
  contacts,
  deviceType,
  wishlistCount,
  appLink,
  getDeeplink,
}: ISideNavMenu): JSX.Element => {
  const [isOpenedMobileMenu, setIsOpenedMobileMenu] = useState(false);

  const { setIsFreeze } = useFreezeBody(false);

  useFreezeBody(isOpenedMobileMenu);

  useEffect(() => {
    /**
     * Добавлено для анимации(через 200мс добавляется свойство transform)
     */
    setIsOpenedMobileMenu(true);
    setIsFreeze(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!appLink) getDeeplink();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onToggle = () => {
    setIsOpenedMobileMenu(false);
    /**
     * Скрываем сайдбар только через 200мс, чтобы появилась анимация
     */
    const closeTimeoutId = setTimeout(() => {
      closeSidebar();
      clearTimeout(closeTimeoutId);
    }, 200);
    setIsFreeze(false);
  };

  const goToDesktopVersion = () => {
    setMobileCookie(true);
    window.location.reload();
  };

  const menu = getMobileMenu({
    isWhitelabel,
    isLoggedIn,
    onToggle,
    onShowAuthPopup,
    onLogOut,
    onShowFeedbackPopup: onOpenWriteUsPopup,
    wishlistCount,
    customContacts: contacts,
  }).filter((item) => item.active);
  return (
    <Modal>
      <StyledContainer data-testid={WRAPPER_TEST_ID}>
        <StyledSidebarWrapper $isOpened={isOpenedMobileMenu}>
          <StyledImage />

          <StyledMenuWrapper>
            <StyledMenuTopPart>
              {menu.map(
                ({
                  icon,
                  text,
                  href,
                  external,
                  onClick,
                  dataTestId,
                  secondIcon,
                }) => (
                  <StyledMenuLink
                    href={href || ''}
                    key={text}
                    target={external ? '_blank' : '_self'}
                    rel="noreferrer"
                    onClick={onClick || (() => null)}
                    data-testid={dataTestId}
                  >
                    <StyledIcon>{icon}</StyledIcon>
                    <StyledMenuText>{text}</StyledMenuText>
                    {secondIcon}
                  </StyledMenuLink>
                ),
              )}
              <StyledFullVersionContainer>
                <StyledMenuItem onClick={goToDesktopVersion}>
                  <StyledIcon>
                    <Monitor />
                  </StyledIcon>
                  <StyledMenuText>
                    {i18n.t('headerMenu.mobile.full_ver')}
                  </StyledMenuText>
                </StyledMenuItem>
                {!isHideFeedback && (
                  <StyledMenuLink href="/agreements">
                    <StyledMenuText>
                      <StyledBottomLink>
                        {i18n.t('headerMenu.mobile.privacy', { nbsp: NBSP })}
                      </StyledBottomLink>
                    </StyledMenuText>
                  </StyledMenuLink>
                )}
              </StyledFullVersionContainer>
              {!isWhitelabel && deviceType === MOBILE_PLATFORMS.ANDROID && (
                <StyledMobileStoreContainer>
                  <MobileAppBanner
                    sidebarMenu
                    appLink={appLink}
                    deviceType={MOBILE_PLATFORMS.ANDROID}
                  />
                  <MobileAppBanner
                    sidebarMenu
                    deviceType={MOBILE_PLATFORMS.RUSTORE}
                    appLink={appLink}
                  />
                  <MobileAppBanner
                    sidebarMenu
                    deviceType={MOBILE_PLATFORMS.APPGALLERY}
                    appLink={appLink}
                  />
                </StyledMobileStoreContainer>
              )}
              {!isWhitelabel && deviceType === MOBILE_PLATFORMS.IOS && (
                <StyledMobileStoreContainer>
                  <MobileAppBanner
                    sidebarMenu
                    deviceType={MOBILE_PLATFORMS.IOS}
                    appLink={appLink}
                  />
                </StyledMobileStoreContainer>
              )}
            </StyledMenuTopPart>
          </StyledMenuWrapper>
        </StyledSidebarWrapper>

        <SideNavBg onClick={onToggle} />
      </StyledContainer>
    </Modal>
  );
};

const StyledFullVersionContainer = styled.div`
  border-top: 1px solid #dbdbdb;
  padding: 5px 0px;
`;

const StyledContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
`;

const StyledImage = styled.div`
  height: 147px;
  background: url('https://cdn.level.travel/lt-modules-assets/search/mobile_menu_image@2x.jpg') -40px -80px
    no-repeat;
  background-size: 362px 251px;
`;

const StyledMenuWrapper = styled.div`
  // вычитаем из 100% высоту изображения, которое в шапке
  height: calc(100dvh - 147px);
  background: #f5f5f5;
  box-shadow: 0px -0.5px 3.5px 0px rgba(0, 0, 0, 0.28);
`;
const StyledMenuItem = styled.div`
  padding: 10px 25px;
  gap: 8px;
  align-items: center;
  display: flex;
  text-decoration: none;
  color: #000;
`;

const StyledSidebarWrapper = styled.div<{ $isOpened: boolean }>`
  position: fixed;
  z-index: 1001;
  top: 0;
  left: -210px;
  width: 210px;
  height: 100vh;
  transition: all 200ms cubic-bezier(0.25, 0.25, 0.75, 0.75);
  ${({ $isOpened }) => $isOpened && `transform: translate3d(210px, 0px, 0px)`}
`;
const StyledMenuTopPart = styled.div`
  position: relative;
  padding-top: 15.5px;
  height: 100%;
  overflow: scroll;
`;

const StyledBottomLink = styled.span`
  color: rgb(124, 136, 161);
`;
const StyledMenuLink = styled(Link)`
  padding: 10px 25px;
  display: flex;
  gap: 8px;
  align-items: center;
  text-decoration: none;
  color: #000;
`;
const StyledIcon = styled.div`
  min-width: 24px;
  height: 24px;
`;
const StyledMenuText = styled.span`
  display: inline-block;
  font-size: 14px;
  vertical-align: middle;
`;

const SideNavBg = styled.div`
  background: rgba(0, 0, 0, 0.5);
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: 1000;
`;

const StyledMobileStoreContainer = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  gap: 5px;
  flex-direction: column;
  align-items: center;
`;

export default SideNavMenu;
